import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'

// Section 1: Opinion questions (Not enough / About right / Too much scale)
const OPINION_QUESTIONS = [
  {
    id: 'Opinion_Protect30Pct',
    label: "Canada's government has made a commitment to permanently protect 30% of our lands and freshwater by 2030. Thinking of nature conservation, would you say protecting 30% is..."
  },
  {
    id: 'Opinion_Protect30PctIntl',
    label: "Canada's government has made an international commitment to permanently protect 30% of our lands and freshwater by 2030. Thinking of nature conservation, would you say protecting 30% is..."
  },
] as const

// Response order
const OPINION_RESPONSE_ORDER = [
  'Not enough',
  'About right',
  'Too much',
] as const

// Legend items
const OPINION_LEGEND = [
  { label: 'Not enough', color: '#005383' },
  { label: 'About right', color: '#bab0ac' },
  { label: 'Too much', color: '#aa0503' },
]

// Q12 exact question wording from topline
const Q12_QUESTION_TEXT = "Thinking of Canada's current plans to expand resource development, how much of Canada's land and freshwater should be permanently protected from large-scale industrial activities (e.g., mining, forestry)?"

export function HowMuchProtected() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  const renderQuestionSection = (
    questions: readonly { id: string; label: string }[],
    responseOrder: readonly string[]
  ) => {
    return questions.map(({ id, label }) => {
      const question = data?.questions[id]
      if (!question) return null

      const rows = breakValues.map(value => {
        const filterKey = buildFilterKey(
          'total',
          selectedBreak,
          isBreakActive ? value : undefined,
          selectedProvince
        )
        let questionData = question.data[filterKey]
        if (!questionData && selectedProvince) {
          const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
          questionData = question.data[fallbackKey]
        }
        return { value, filterKey, data: questionData }
      }).filter(row => row.data)

      if (rows.length === 0) return null

      return (
        <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            {label}
          </h3>

          <div className="space-y-2">
            {rows.map(row => {
              const sortedResponses = responseOrder.map(lbl => {
                const response = row.data!.responses.find(r => r.label === lbl)
                return response || { label: lbl, percentage: 0 }
              }).filter(r => r.percentage > 0 || row.data!.responses.some(resp => resp.label === r.label))

              return (
                <div key={row.filterKey} className="flex items-center gap-2">
                  <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                    {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                  </div>
                  <div className="flex-1">
                    <HorizontalStackedBar
                      segments={sortedResponses}
                      scaleType="opinion"
                      height={isBreakActive ? 24 : 28}
                      showLabels={false}
                      normalize={true}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          How Much Should Be Protected
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-4">
          Opinions on land and freshwater protection targets
        </p>

        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          {OPINION_LEGEND.map(item => (
            <div key={item.label} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: item.color }} />
              <span className="text-sm text-slate-600 dark:text-slate-400">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-8">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            renderQuestionSection(OPINION_QUESTIONS, OPINION_RESPONSE_ORDER)
          )}
        </div>
      </div>

      {/* Q12: How much land should be protected - Median/Mean with bars */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
          How Much Land Should Be Protected?
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mb-6 text-sm">
          {Q12_QUESTION_TEXT}
        </p>

        {/* Legend */}
        <div className="flex gap-6 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#3b82f6' }} />
            <span className="text-sm text-slate-600 dark:text-slate-400">Median</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: '#f97316' }} />
            <span className="text-sm text-slate-600 dark:text-slate-400">Mean</span>
          </div>
        </div>

        {/* Bars visualization */}
        <div className="space-y-4">
          {(() => {
            const question = data?.questions['PercentLandProtected'] as { stats?: Record<string, { median: number; mean: number; sampleSize: number }> } | undefined
            if (!question?.stats) return (
              <p className="py-4 text-center text-slate-500 dark:text-slate-400 italic">
                Median/Mean data not available - please re-run aggregation script
              </p>
            )

            const rows = breakValues.map(value => {
              const filterKey = buildFilterKey(
                'total',
                selectedBreak,
                isBreakActive ? value : undefined,
                selectedProvince
              )
              let stats = question.stats?.[filterKey]
              if (!stats && selectedProvince) {
                const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
                stats = question.stats?.[fallbackKey]
              }
              return { value, filterKey, stats }
            }).filter(row => row.stats)

            if (rows.length === 0) return (
              <p className="py-4 text-center text-slate-500 dark:text-slate-400 italic">
                No data for current filters
              </p>
            )

            // Color functions: Blue for Median (stable/robust), Orange for Mean (can be skewed)
            // This is data viz best practice - blue/orange are colorblind-friendly and contrasting
            const getMedianColor = (value: number) => {
              // Blue gradient: lighter for lower values, darker for higher values
              const intensity = Math.min(1, value / 100)
              // Interpolate from light blue (#93c5fd) to dark blue (#1e40af)
              const r = Math.round(147 - (147 - 30) * intensity)
              const g = Math.round(197 - (197 - 64) * intensity)
              const b = Math.round(253 - (253 - 175) * intensity)
              return `rgb(${r}, ${g}, ${b})`
            }

            const getMeanColor = (value: number) => {
              // Orange gradient: lighter for lower values, darker for higher values
              const intensity = Math.min(1, value / 100)
              // Interpolate from light orange (#fed7aa) to dark orange (#c2410c)
              const r = Math.round(254 - (254 - 194) * intensity)
              const g = Math.round(215 - (215 - 65) * intensity)
              const b = Math.round(170 - (170 - 12) * intensity)
              return `rgb(${r}, ${g}, ${b})`
            }

            return rows.map(row => {
              const { median, mean } = row.stats!
              return (
                <div key={row.filterKey} className="space-y-1">
                  {/* Row label */}
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                  </div>
                  {/* Median bar (blue) */}
                  <div className="flex items-center gap-2">
                    <div className="w-16 text-xs text-slate-500 dark:text-slate-400 text-right">Median</div>
                    <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-700 rounded relative">
                      <div
                        className="h-full rounded flex items-center justify-end pr-2"
                        style={{ width: `${median}%`, backgroundColor: getMedianColor(median) }}
                      >
                        <span className="text-xs font-semibold text-white drop-shadow-sm">
                          {median.toFixed(0)}% protected
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Mean bar (orange) */}
                  <div className="flex items-center gap-2">
                    <div className="w-16 text-xs text-slate-500 dark:text-slate-400 text-right">Mean</div>
                    <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-700 rounded relative">
                      <div
                        className="h-full rounded flex items-center justify-end pr-2"
                        style={{ width: `${mean}%`, backgroundColor: getMeanColor(mean) }}
                      >
                        <span className="text-xs font-semibold text-white drop-shadow-sm">
                          {mean.toFixed(0)}% protected
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          })()}
        </div>
      </div>
    </div>
  )
}
